// Copyright (c) StrangeBee 2024

import { ICONS } from '@Constants/iconsApp';
import client from '@Client';
import { downloadFile } from '@Helpers/download';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { PlatformTable } from '@Organisms/PlatformStatus';
import { useGenericQuery } from '@Queries/genericQueries';
import { Tag } from 'antd';
import React, { ReactElement, useCallback, useMemo } from 'react';
import moment from 'moment';
import { useLocalMessage } from '@Helpers/locale';
import { DatabaseCheckOutput, SchemaStatus, StatusOutput, PermissionsEnum } from 'thehive-sdk';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { THLayout } from '@Molecules/THLayout';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { computeEventList } from '@Organisms/PlatformStatus/eventList';
import { useDrawer } from '@Hooks/useDrawer';
import { PlatformDatabaseDetails } from '@Organisms/PlatformStatus/PlatformDatabaseDetails/PlatformDatabaseDetails';
import { PlatformMonitorStatus } from '@Organisms/PlatformStatus/PlatformMonitorStatus/PlatformMonitorStatus';
import usePageTitle from '@Hooks/usePageTitle';

import './PlatformStatusDetails.scss';

type DatabaseCheckEntityOutput = DatabaseCheckOutput & { thEntityName: string };

const PlatformStatusDetails = (): ReactElement => {
    const i = useLocalMessage();

    const { visible, data: drawerData, setData, setVisible } = useDrawer<DatabaseCheckEntityOutput>({});

    const { data } = useGenericQuery<StatusOutput>({
        retry: false,
        key: ['get-status-verbose'],
        fetchCallback: useCallback(() => client.StatusController.getVerbose(), []),
    });

    const { schemaStatus = [] } = data || {};

    const { data: monitorStatus } = useGenericQuery({
        fetchCallback: () => client.PlatformStatusController.monitor(),
        key: ['get-monitor-status'],
    });

    const {
        data: platformStats,
        refetch: refetchPlatformStats,
        isFetching: statsIsLoading,
    } = useGenericQuery({
        fetchCallback: () => client.PlatformStatusController.stats(),
        key: ['get-platform-stats'],
    });

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.PlatformStatusController,
                locale: i,
                onSuccess: (_, message) => {
                    message && openMessage('success', message);
                    refetchPlatformStats();
                },
                onError: handleNotificationError,
            }),
        [i, refetchPlatformStats],
    );

    const handleExport = (): void => {
        if (platformStats && schemaStatus) {
            const exportObject = { checkStatus: platformStats, schemaStatus };
            const fileName = `Platform-Status-Report-${moment().format('YYYYMMDD-HH:mmZ')}`;

            downloadFile(fileName + '.json', JSON.stringify(exportObject), 'application/json');
        }
    };

    const platformStatsParsed = useMemo(
        () =>
            Object.entries(platformStats || {}).map(([thEntityName, databaseCheck]) => ({
                thEntityName,
                ...databaseCheck,
            })),
        [platformStats],
    );

    useDrawer.watch(platformStatsParsed, () => {
        if (platformStats) {
            setData(platformStatsParsed.find(({ thEntityName }) => thEntityName === drawerData?.thEntityName));
        }
    });

    usePageTitle(i('page-title.admin.platform.status'));

    return (
        <THLayout>
            <THLayout.Content>
                <div className="platform-status-details">
                    <div className="platform-status-details__title">
                        <div>{i('platform.title.label')}</div>
                        <div>
                            <THButton type="primary" onClick={handleExport}>
                                {i('platform.title.export')}
                            </THButton>
                        </div>
                    </div>

                    {monitorStatus && (
                        <div className="platform-status-details__monitor">
                            <div className="platform-status-details__table-title">{i('platform.monitor.title')}</div>
                            <div className="platform-status-details__monitor__list">
                                {monitorStatus?.map((data, index) => (
                                    <PlatformMonitorStatus key={`monitor-${index}`} data={data} />
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="platform-status-details__table-header">
                        <div className="platform-status-details__table-title">{i('platform.schema.title')}</div>
                    </div>

                    <div className="platform-status-details__table">
                        <PlatformTable<SchemaStatus>
                            dataSource={schemaStatus}
                            columns={[
                                {
                                    title: i('platform.schema.columns.status'),
                                    className: 'schema-table__column__status',
                                    render: (_, { error }) =>
                                        !error ? <Tag color="green">OK</Tag> : JSON.stringify(error),
                                },
                                {
                                    title: i('platform.schema.columns.schema'),
                                    className: 'schema-table__column__name',
                                    render: (_, { name }) => name,
                                },
                                {
                                    title: i('platform.schema.columns.version'),
                                    className: 'schema-table__column__version',
                                    render: (_, { currentVersion }) => currentVersion,
                                },
                            ]}
                            rowKey="name"
                        />
                    </div>

                    <div className="platform-status-details__table-header">
                        <div className="platform-status-details__table-title">{i('platform.check.title')}</div>
                    </div>

                    <div className="platform-status-details__table">
                        <PlatformTable<DatabaseCheckOutput>
                            dataSource={
                                platformStats
                                    ? Object.entries(platformStats).map(([thEntityName, databaseCheck]) => ({
                                          thEntityName,
                                          ...databaseCheck,
                                      }))
                                    : []
                            }
                            columns={[
                                {
                                    title: i('platform.check.columns.control'),
                                    className: 'stats-table__column__name',
                                    render: (_, { thEntityName }) => thEntityName,
                                },
                                {
                                    title: i('platform.index.columns.entities'),
                                    className: 'stats-table__column__count',
                                    render: (_, { count }) => <Tag>{count}</Tag>,
                                },
                                {
                                    className: 'stats-table__column__actions',
                                    render: (_, data) => (
                                        <THButton
                                            type="primary"
                                            disabled={!data.check}
                                            onClick={() => {
                                                setVisible(true);
                                                setData(data);
                                            }}
                                        >
                                            {i('common.see-details')}
                                        </THButton>
                                    ),
                                },
                            ]}
                            loading={statsIsLoading}
                        />
                    </div>

                    {drawerData && (
                        <PlatformDatabaseDetails
                            data={drawerData}
                            eventList={eventList}
                            visible={visible}
                            onVisibleChange={(visible) => setVisible(visible)}
                        />
                    )}
                </div>
            </THLayout.Content>
        </THLayout>
    );
};

const path = RouteBuilder.compute('administration.status');

export const Route: RouteType = {
    path,
    menu: {
        label: 'mainLayout.adminMenu.status',
        icon: <ICONS.PlatformIcon />,
    },
    breadcrumb: { label: 'breadcrumb.global.platform' },
    content: <PlatformStatusDetails />,
    permissionNeeded: [PermissionsEnum.MANAGE_PLATFORM],
    space: ['admin'],
    exact: true,
};
